import type { AppointmentDetail, CatalogItem, ChartData, ChartField, ChartItem, ChartOption, ChartView, EditAppointmentInput } from "@vaulted/shared";
import { fieldOffers } from "@vaulted/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, errorMessage, post, put } from "../api";
import { newChartItem } from "../pages/Chart";
import { useSession } from "../session";
import type { TreatmentLine } from "./TreatmentDrawing";
import { Alert, Button, Checkbox, Dialog, Field, cx } from "./ui";

/**
 * The visit screen's Treatment card: each line's lot, taking things off, adding to the bag or an
 * injection, and swapping the drip. Changes to what's booked go through the booking (its rules,
 * the nurse's time, the bill); the unsigned chart follows it.
 */

type CatalogField = Extract<ChartField, { kind: "catalog" }>;
type Services = EditAppointmentInput["services"];

const items = (data: ChartData) => Object.values(data).flatMap((v) => (Array.isArray(v) && v.length && typeof v[0] === "object" ? (v as ChartItem[]) : []));
const ADD_LABEL: Record<string, string> = { addons: "Add to the bag", injections: "Add an injection", medications: "Add a medication or vitamin", recovery: "Add recovery" };

export function useTreatmentEdits({ a: maybe, c, draft, setDraft, catalogItems }: { a: AppointmentDetail | undefined; c: ChartView | null; draft: ChartData; setDraft: (d: ChartData) => void; catalogItems: CatalogItem[] }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const manage = can("appointments.manage");
  const chartEditable = !!c && c.status === "draft" && can("charts.write");
  const [error, setError] = useState<unknown>(null);
  // Called before the visit has loaded (hooks come first); nothing can change until it has.
  const a = maybe ?? ({ id: "", services: [], status: "cancelled", context: "clinic", start: "", staffId: null, priceCents: 0, priceOverrideReason: null } as unknown as AppointmentDetail);
  const fields = (c?.template.sections.flatMap((s) => s.fields) ?? []).filter((f): f is CatalogField => f.kind === "catalog");

  const refresh = async () => {
    await Promise.all([["appointment", a.id], ["chart"], ["checkout", a.id], ["calendar"]].map((k) => qc.invalidateQueries({ queryKey: k })));
  };
  const saveChart = async (data: ChartData) => {
    setDraft(data);
    if (!c) return;
    const v = await put<ChartView>(`/api/charts/${c.id}`, { data, baseUpdatedAt: (qc.getQueryData<ChartView>(["chart", c.id]) ?? c).updatedAt });
    qc.setQueryData(["chart", c.id], v);
  };
  /** Off the chart (before the booking changes: a picked lot would block it). */
  const stripped = (ids: string[]) => Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, Array.isArray(v) && v.length && typeof v[0] === "object" ? (v as ChartItem[]).filter((x) => !x.catalogItemId || !ids.includes(x.catalogItemId)) : v])) as ChartData;

  /** Change what's booked; a broken visit rule or a clash with the nurse's next visit asks why. */
  const editServices = async (services: Services, opts: { reason?: string; newPrice?: boolean; servicesReason?: string } = {}) => {
    const body: EditAppointmentInput = {
      services,
      staffId: a.staffId,
      start: a.start,
      priceCents: !opts.newPrice && a.priceOverrideReason ? a.priceCents : null,
      priceReason: "",
      servicesReason: opts.servicesReason ?? "",
      forceReason: null,
      reason: opts.reason ?? "",
      offMenuReason: "",
    };
    for (let tries = 0; tries < 4; tries++) {
      try {
        await post(`/api/appointments/${a.id}/edit`, body);
        return true;
      } catch (e) {
        const rules = e instanceof ApiError && e.code === "visit_rules";
        const offMenu = e instanceof ApiError && e.code === "off_menu" && !body.offMenuReason;
        const clash = e instanceof ApiError && e.status === 409 && a.context === "clinic" && !body.forceReason;
        if (!rules && !clash && !offMenu) throw e;
        const why = window.prompt(`${e.message}\n\nReason to go ahead anyway (it's logged):`)?.trim();
        if (!why) return false;
        if (rules) body.servicesReason = why;
        else if (offMenu) body.offMenuReason = why;
        else body.forceReason = why;
      }
    }
    return false;
  };
  const booked = (id: string) => a.services.some((s) => s.serviceId === id || s.addOns.some((x) => x.id === id));
  const drips = a.services.filter((s) => catalogItems.find((i) => i.id === s.serviceId)?.effectiveChartSection === "cocktail").map((s) => s.serviceId);

  const remove = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      setError(null);
      if (!booked(id)) return saveChart(stripped([id]));
      const reason = window.prompt(`${name} wasn't given? It comes off the visit and the bill. Why?`, "Client declined")?.trim();
      if (!reason) return;
      if (c) await saveChart(stripped([id]));
      const services = a.services.filter((s) => s.serviceId !== id).map((s) => ({ serviceId: s.serviceId, addOnIds: s.addOns.map((x) => x.id).filter((x) => x !== id) }));
      if (!services.length) throw new Error("A visit needs at least one service. Swap it instead, or cancel the visit.");
      await editServices(services, { reason: `Not given: ${reason}` });
    },
    onSettled: refresh,
    onError: setError,
  });

  const add = useMutation({
    mutationFn: async ({ o, f }: { o: ChartOption; f: CatalogField }) => {
      setError(null);
      const item = catalogItems.find((i) => i.id === o.catalogItemId);
      // Without "Manage appointments" (a nurse): on the chart only, marked for the bill.
      if (!manage || !item || (item.type !== "add_on" && item.type !== "service")) {
        if (c) await saveChart({ ...draft, [f.key]: [...((draft[f.key] as ChartItem[] | undefined) ?? []), newChartItem(o, c.lots)] });
        return;
      }
      const services: Services = a.services.map((s) => ({ serviceId: s.serviceId, addOnIds: s.addOns.map((x) => x.id) }));
      if (item.type === "service") services.push({ serviceId: item.id, addOnIds: [] });
      else {
        // An add-on rides on the service it's offered with (else the first).
        const on = services.find((s) => { const svc = catalogItems.find((i) => i.id === s.serviceId); return item.attachToServiceIds.includes(s.serviceId) || (!!svc?.categoryId && item.attachToCategoryIds.includes(svc.categoryId)); }) ?? services[0]!;
        on.addOnIds.push(item.id);
      }
      await editServices(services);
    },
    onSettled: refresh,
    onError: setError,
  });

  const swap = useMutation({
    mutationFn: async ({ from, to, drop, reason }: { from: string; to: string; drop: string[]; reason: string }) => {
      setError(null);
      if (c) await saveChart(stripped([from, ...drop]));
      const services = a.services.map((s) => (s.serviceId === from ? { serviceId: to, addOnIds: s.addOns.map((x) => x.id).filter((x) => !drop.includes(x)) } : { serviceId: s.serviceId, addOnIds: s.addOns.map((x) => x.id) }));
      await editServices(services, { newPrice: true, servicesReason: reason, reason: "Drip swapped" });
    },
    onSettled: refresh,
    onError: setError,
  });

  const pickLot = useMutation({
    mutationFn: async ({ l, lotId }: { l: TreatmentLine; lotId: string }) => {
      if (!c) return;
      const data = structuredClone(draft);
      for (const it of items(data)) {
        if (it.catalogItemId !== l.itemId) continue;
        const g = it.ingredients?.find((x) => x.formularyItemId === l.formularyItemId);
        const lot = (c.lots[l.formularyItemId] ?? []).find((x) => x.id === lotId);
        if (g) Object.assign(g, lot ? { lotId: lot.id, lot: lot.lotNumber, expiration: lot.expiresOn ?? "" } : { lotId: null, lot: "", expiration: "" });
      }
      await saveChart(data);
    },
    onError: setError,
  });

  const busy = remove.isPending || add.isPending || swap.isPending || pickLot.isPending;
  // Services can change until the visit is paid or its chart signed.
  const canChange = (manage || chartEditable) && !["completed", "cancelled", "no_show"].includes(a.status) && (!c || c.status === "draft");
  return { fields, drips, booked, remove, add, swap, pickLot, busy, error, canChange, manage, chartEditable };
}

const fmtDate = (d: string) => (d.length === 10 ? new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit", timeZone: "UTC" }) : d);

/** One line's lot (medications and bags, picked from the shelf, soonest to expire first) and, on its first line, × or Swap. */
export function LineControls({ l, first, c, draft, edits, onSwap }: { l: TreatmentLine; first: boolean; c: ChartView | null; draft: ChartData; edits: ReturnType<typeof useTreatmentEdits>; onSwap: (id: string) => void }) {
  const g = items(c?.status === "draft" ? draft : (c?.data ?? {})).find((x) => x.catalogItemId === l.itemId)?.ingredients?.find((x) => x.formularyItemId === l.formularyItemId);
  const lots = c?.lots[l.formularyItemId] ?? [];
  const drip = edits.drips.includes(l.itemId);
  const lot = !c ? null : !g ? (
    // A bag on a chart from before bags had lots: it came off the shelf without one.
    <span className="text-muted">{l.how === "base" ? "No lot recorded" : "No lot needed"}</span>
  ) : edits.chartEditable ? (
    <select
      aria-label={`Lot for ${l.name}`}
      className={cx("max-w-full rounded-[var(--radius-control)] border bg-white px-2 py-1.5 text-sm", g.lotId || g.lot ? "border-line" : "border-wait text-wait")}
      disabled={edits.busy}
      value={g.lotId ?? ""}
      onChange={(e) => edits.pickLot.mutate({ l, lotId: e.target.value })}
    >
      <option value="">{lots.length ? "Pick the lot…" : g.lot ? `Lot ${g.lot} (typed)` : "None on the shelf"}</option>
      {lots.map((x) => (
        <option key={x.id} value={x.id}>
          {x.lotNumber ? `Lot ${x.lotNumber}` : "No lot #"}
          {x.expiresOn ? ` · exp ${fmtDate(x.expiresOn)}` : ""}
          {x.status === "expiring" ? " · expiring soon" : ""}
          {x.open ? " · open" : ""}
        </option>
      ))}
    </select>
  ) : (
    <span className="text-ink-soft">{g.lot || g.expiration ? `Lot ${g.lot || "—"}${g.expiration ? ` · exp ${fmtDate(g.expiration)}` : ""}` : "No lot recorded"}</span>
  );
  const canTakeOff = first && edits.canChange && !drip && (edits.manage || !edits.booked(l.itemId));
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {lot}
      {first && drip && edits.canChange && edits.manage && (
        <Button type="button" variant="quiet" disabled={edits.busy} onClick={() => onSwap(l.itemId)}>
          Swap drip
        </Button>
      )}
      {canTakeOff && (
        <button type="button" disabled={edits.busy} className="ml-auto rounded-full px-2 text-lg leading-none text-muted hover:text-danger" aria-label={`Take ${l.name} off`} title="Take off" onClick={() => edits.remove.mutate({ id: l.itemId, name: catalogName(l, c) })}>
          ×
        </button>
      )}
    </div>
  );
}

const catalogName = (l: TreatmentLine, c: ChartView | null) => c?.options.find((o) => o.catalogItemId === l.itemId)?.name ?? l.name;

/** Event visits: the event's menu first; anything else is marked (staff give a reason to add it). */
const menuFirst = (list: { id: string; name: string }[], menu: string[] | null) =>
  !menu ? list : [...list.filter((o) => menu.includes(o.id)), ...list.filter((o) => !menu.includes(o.id)).map((o) => ({ ...o, note: "Off the menu" }))];

/** "+ Add to the bag…": type to find it, tap to add. */
export function AddSearch({ label, options, disabled, onPick }: { label: string; options: { id: string; name: string; note?: string }[]; disabled?: boolean; onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const shown = options.filter((o) => o.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);
  return (
    <div className="relative">
      <input
        className="w-full rounded-[var(--radius-control)] border border-dashed border-line bg-white px-3 py-2 text-sm placeholder:text-ink-soft"
        placeholder={`+ ${label}…`}
        disabled={disabled}
        value={q}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => (setQ(e.target.value), setOpen(true))}
      />
      {open && shown.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-[var(--radius-control)] border border-line bg-white shadow-lg">
          {shown.map((o) => (
            <li key={o.id}>
              <button type="button" className="flex w-full justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-mist" onMouseDown={(e) => e.preventDefault()} onClick={() => (onPick(o.id), setQ(""), setOpen(false))}>
                <span>{o.name}</span>
                {o.note && <span className="text-muted">{o.note}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The add boxes under the list: one per chart list (not the drip). */
export function TreatmentAdds({ c, draft, edits, menu }: { c: ChartView; draft: ChartData; edits: ReturnType<typeof useTreatmentEdits>; menu: string[] | null }) {
  if (!edits.canChange) return null;
  const have = new Set(items(draft).map((x) => x.catalogItemId));
  return (
    <div className="flex flex-col gap-2">
      {edits.fields
        .filter((f) => f.key !== "cocktail")
        .map((f) => (
          <AddSearch
            key={f.key}
            label={ADD_LABEL[f.key] ?? `Add to ${f.label.toLowerCase()}`}
            disabled={edits.busy}
            options={menuFirst(c.options.filter((o) => fieldOffers(f, o) && !have.has(o.catalogItemId)).sort((x, y) => x.name.localeCompare(y.name)).map((o) => ({ id: o.catalogItemId, name: o.name })), menu)}
            onPick={(id) => edits.add.mutate({ o: c.options.find((o) => o.catalogItemId === id)!, f })}
          />
        ))}
      {edits.error != null && <Alert>{errorMessage(edits.error)}</Alert>}
    </div>
  );
}

/** Swapping the drip: pick the new one; add-ons it doesn't offer come off unless kept (with a reason). */
export function SwapDrip({ a, from, catalogItems, edits, onClose }: { a: AppointmentDetail; from: string; catalogItems: CatalogItem[]; edits: ReturnType<typeof useTreatmentEdits>; onClose: () => void }) {
  const [to, setTo] = useState<string | null>(null);
  const [keep, setKeep] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const old = catalogItems.find((i) => i.id === from);
  const next = to ? catalogItems.find((i) => i.id === to) : null;
  const drips = catalogItems.filter((i) => i.type === "service" && i.status === "active" && i.effectiveChartSection === "cocktail" && i.id !== from && (a.context !== "clinic" || i.availableClinic) && (a.context !== "mobile" || i.availableMobile));
  const addOns = a.services.find((s) => s.serviceId === from)?.addOns ?? [];
  const offered = (id: string) => {
    const x = catalogItems.find((i) => i.id === id);
    return !!next && !!x && (x.attachToServiceIds.includes(next.id) || (!!next.categoryId && x.attachToCategoryIds.includes(next.categoryId)));
  };
  const notOffered = addOns.filter((x) => !offered(x.id));
  const kept = notOffered.filter((x) => keep.includes(x.id));
  return (
    <Dialog
      title={`Swap ${old?.name ?? "the drip"}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Back
          </Button>
          <Button
            variant="primary"
            busy={edits.swap.isPending}
            disabled={!to || (kept.length > 0 && reason.trim().length < 3)}
            onClick={() => edits.swap.mutate({ from, to: to!, drop: notOffered.filter((x) => !keep.includes(x.id)).map((x) => x.id), reason: kept.length ? reason.trim() : "" }, { onSuccess: onClose })}
          >
            Swap
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {next ? (
          <div className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] border border-line px-3 py-2">
            <span className="font-semibold">{next.name}</span>
            <Button variant="quiet" onClick={() => setTo(null)}>
              Change
            </Button>
          </div>
        ) : (
          <AddSearch label="Find the new drip" options={menuFirst(drips.map((d) => ({ id: d.id, name: d.name })), a.eventMenuIds)} onPick={(id) => (setTo(id), setKeep([]))} />
        )}
        {next && notOffered.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-ink-soft">Not offered with {next.name}: they come off unless you keep them.</p>
            {notOffered.map((x) => (
              <Checkbox key={x.id} label={`Keep ${x.name}`} checked={keep.includes(x.id)} onChange={(on) => setKeep(on ? [...keep, x.id] : keep.filter((k) => k !== x.id))} />
            ))}
            {kept.length > 0 && <Field label="Why keep them anyway (logged)" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </div>
        )}
        {next && <p className="text-sm text-muted">The price, bill, chart, and visit length follow the new drip. Lots picked for the old one are dropped.</p>}
        {edits.swap.error != null && <Alert>{errorMessage(edits.swap.error)}</Alert>}
      </div>
    </Dialog>
  );
}
